/**
 * Frame budget tracking.
 *
 * The whole project targets 60fps on a mid-range phone: ~8ms for logic and ~8ms
 * for render. This exists from day one so a regression is visible the day it
 * lands rather than during a Phase 10 panic.
 */

const SAMPLE_WINDOW = 60;

export interface PerfSnapshot {
  readonly fps: number;
  readonly frameMs: number;
  readonly logicMs: number;
  readonly renderMs: number;
  /** True when the averaged frame time exceeds the 60fps budget. */
  readonly overBudget: boolean;
}

export const FRAME_BUDGET_MS = 1000 / 60;

export class PerfMonitor {
  private readonly frameSamples: number[] = [];
  private readonly logicSamples: number[] = [];
  private readonly renderSamples: number[] = [];

  record(frameMs: number, logicMs: number, renderMs: number): void {
    push(this.frameSamples, frameMs);
    push(this.logicSamples, logicMs);
    push(this.renderSamples, renderMs);
  }

  snapshot(): PerfSnapshot {
    const frameMs = average(this.frameSamples);
    return {
      fps: frameMs > 0 ? 1000 / frameMs : 0,
      frameMs,
      logicMs: average(this.logicSamples),
      renderMs: average(this.renderSamples),
      overBudget: frameMs > FRAME_BUDGET_MS * 1.1,
    };
  }

  reset(): void {
    this.frameSamples.length = 0;
    this.logicSamples.length = 0;
    this.renderSamples.length = 0;
  }
}

export function formatPerf(snapshot: PerfSnapshot): string {
  const { fps, frameMs, logicMs, renderMs } = snapshot;
  return [
    `${fps.toFixed(0).padStart(3)} fps  ${frameMs.toFixed(2)}ms`,
    `logic ${logicMs.toFixed(2)}ms  draw ${renderMs.toFixed(2)}ms`,
  ].join('\n');
}

function push(samples: number[], value: number): void {
  samples.push(value);
  if (samples.length > SAMPLE_WINDOW) samples.shift();
}

function average(samples: readonly number[]): number {
  if (samples.length === 0) return 0;
  let total = 0;
  for (const sample of samples) total += sample;
  return total / samples.length;
}
